import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom-v5-compat';
import { useDispatch } from 'react-redux';
import { Grid } from '@mui/material';
import { useForm, FormProvider } from 'react-hook-form';
import {
    AddressInputBlock,
    DrawerBody,
    DrawerHeading,
    DrawerFooter,
    CreateTagButton,
    TagMultiselect,
    UncontrolledGenericDrawer
} from '@/components/common';
import { TextField, PhoneNumber } from '@/components/rhf-mui';
import {
    useToastNotification,
    useLocaleCountry,
    useUrlParams,
    useDrawers
} from '@/hooks';
import { ClientsActive, SaveIcon } from '@/resources/icons';
import {
    addClient,
    updateClient,
    getPaginatedClients,
    getPaginatedTags
} from '@/modules/dataWrangler/dataWrangler';
import { closeDrawer } from '@/state/actions';
import { emailRegexPattern } from '@/util';
import { CLIENT_BASE_PARAM, TAG_BASE_PARAM } from '@/constants';
import useStyles from './styles';

const ClientDrawer = () => {
    const businessCountry = useLocaleCountry();

    const location = useLocation();
    const { getUrlParams } = useUrlParams();
    const currentClientUrlParams = getUrlParams({ base: CLIENT_BASE_PARAM });
    const currentTagUrlParams = getUrlParams({ base: TAG_BASE_PARAM });

    const { getDrawer } = useDrawers();

    const [saving, setSaving] = useState(false);
    const [defaultPhoneCountry, setDefaultPhoneCountry] = useState('');

    const clientDrawer = getDrawer('client');
    const open = Boolean(clientDrawer);
    const title = clientDrawer?.title || '';
    const updating = clientDrawer?.updating || false;
    const client = clientDrawer?.client;
    const updatedTags = clientDrawer?.updatedTags;
    const currentValues = clientDrawer?.currentValues;

    const classes = useStyles();
    const dispatch = useDispatch();

    const methods = useForm({ mode: 'onBlur' });
    const { setValue, watch, clearErrors } = methods;
    const { triggerToast } = useToastNotification();

    const handleCloseDrawer = () => {
        dispatch(closeDrawer('client'));
        clearErrors();
    };

    useEffect(() => {
        setValue('name', currentValues?.name || client?.name || '');
        setValue(
            'companyName',
            currentValues?.companyName || client?.companyName || ''
        );
        setValue(
            'emailAddress',
            currentValues?.emailAddress || client?.emailAddress || ''
        );
        setValue(
            'phoneNumber',
            currentValues?.phoneNumber || client?.phoneNumber || ''
        );
        setValue('billingAddress', {
            line1:
                currentValues?.billingAddress?.line1 ||
                client?.billingAddress?.line1 ||
                '',
            line2:
                currentValues?.billingAddress?.line2 ||
                client?.billingAddress?.line2 ||
                '',
            city:
                currentValues?.billingAddress?.city ||
                client?.billingAddress?.city ||
                '',
            state:
                currentValues?.billingAddress?.state ||
                client?.billingAddress?.state ||
                '',
            postalCode:
                currentValues?.billingAddress?.postalCode ||
                client?.billingAddress?.postalCode ||
                '',
            country:
                currentValues?.billingAddress?.country ||
                client?.billingAddress?.country ||
                businessCountry?.code,
            customAddress:
                currentValues?.customAddress ||
                client?.billingAddress?.customAddress ||
                ''
        });
        setValue('tags', updatedTags || client?.tags || []);
    }, [
        setValue,
        updatedTags,
        currentValues,
        client,
        businessCountry?.code,
        open
    ]);

    const saveClient = async (fields) => {
        setSaving(true);

        // Remove "tags" key to replace with "tagIds" below
        const { tags, ...clientFields } = fields;

        // Strip out only the tagIds to add to client object
        const tagIds = fields?.tags?.map((tagData) => tagData?.tagId);

        // customFields are not included in the client drawer form, so must be added manually
        const customFields = client?.customFields || [];

        if (updating) {
            await updateClient({
                ...clientFields,
                phoneNumber:
                    fields.phoneNumber === null ? '' : fields.phoneNumber,
                clientId: client.clientId,
                tagIds,
                customFields
            });
        } else {
            await addClient({
                ...clientFields,
                tagIds,
                customFields
            });
        }

        if (location.pathname === '/clients') {
            getPaginatedClients(currentClientUrlParams);
        }

        if (location.pathname === '/items/tags') {
            getPaginatedTags(currentTagUrlParams);
        }

        setSaving(false);
        handleCloseDrawer();

        triggerToast({
            message: updating
                ? `Client "${fields.name}" updated`
                : `Client "${fields.name}" saved`,
            action: 'saved',
            icon: <SaveIcon />
        });
    };

    const handleAddress = (address) => {
        setValue('billingAddress', address);
    };

    return (
        <UncontrolledGenericDrawer
            open={open}
            permanent={false}
            close={handleCloseDrawer}
            id="client"
        >
            <FormProvider {...methods}>
                <form
                    onSubmit={methods.handleSubmit(saveClient)}
                    className={classes.form}
                >
                    <div>
                        <DrawerHeading
                            startAdornment={<ClientsActive />}
                            title={title}
                        />
                        <DrawerBody className={classes.itemList}>
                            <Grid item>
                                <TextField
                                    name="name"
                                    label="Name"
                                    rules={{
                                        required: 'This field is required.'
                                    }}
                                    fullWidth
                                />
                            </Grid>

                            <Grid item>
                                <TextField
                                    name="companyName"
                                    label="Business Name"
                                    inputProps={{ maxLength: 50 }}
                                    fullWidth
                                />
                            </Grid>

                            <Grid item>
                                <TextField
                                    name="emailAddress"
                                    label="Email Address"
                                    inputProps={{ maxLength: 50 }}
                                    fullWidth
                                    rules={{
                                        pattern: {
                                            value: emailRegexPattern,
                                            message: 'Must be a valid email'
                                        }
                                    }}
                                />
                            </Grid>

                            <Grid item>
                                <PhoneNumber
                                    name="phoneNumber"
                                    label="Phone Number"
                                    defaultCountry={
                                        defaultPhoneCountry ||
                                        businessCountry?.code
                                    }
                                    setDefaultPhoneCountry={
                                        setDefaultPhoneCountry
                                    }
                                    fullWidth
                                />
                            </Grid>

                            <AddressInputBlock
                                setAddress={handleAddress}
                                address={watch('billingAddress')}
                                name="billingAddress"
                            />
                            <Grid item>
                                <TagMultiselect />
                            </Grid>
                            <Grid item container justifyContent="flex-end">
                                <CreateTagButton
                                    type="client"
                                    item={client}
                                    currentValues={watch()}
                                    title={title}
                                    edit={updating}
                                />
                            </Grid>
                        </DrawerBody>
                    </div>

                    <DrawerFooter
                        submitButtonId="Addclient"
                        acceptOptions={{
                            label: updating ? 'Update Client' : 'Add Client',
                            type: 'submit'
                        }}
                        cancelOptions={{
                            label: 'Cancel',
                            action: handleCloseDrawer
                        }}
                        saving={saving}
                    />
                </form>
            </FormProvider>
        </UncontrolledGenericDrawer>
    );
};

export default ClientDrawer;
